// The ages of the world along the top: one button per age, laid along the scroll's own time. The age you are
// in is lit; click one to fill the screen with it, and the others shrink into the scroll's rolled ends.

import type { Chart } from "@hoee/chart";
import type { Era } from "@hoee/schema";
import type { Camera } from "../view/camera.ts";

interface Tab {
  era: Era;
  button: HTMLButtonElement;
  shown: { x: number; w: number; active: boolean; narrow: boolean };
}

export class ErasBand {
  readonly el: HTMLElement;
  private readonly tabs: Tab[];

  constructor(chart: Chart, onPick: (era: Era) => void) {
    this.el = document.createElement("nav");
    this.el.className = "eras";
    this.el.setAttribute("aria-label", "Ages of the world");
    this.tabs = chart.eras.map((era) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "era";
      button.textContent = era.name;
      button.title = `${era.name}, ${fmt(era.from)} – ${fmt(era.to)}`;
      button.addEventListener("click", () => onPick(era));
      this.el.append(button);
      return { era, button, shown: { x: -1, w: -1, active: false, narrow: false } };
    });
  }

  /** The age that fills the screen now, if one does. */
  current(camera: Camera): Era | null {
    const { x0, x1 } = camera.inner;
    const from = camera.yearAt(x0),
      to = camera.yearAt(x1);
    const span = to - from;
    return (
      this.tabs.find(({ era }) => {
        const overlap = Math.min(era.to, to) - Math.max(era.from, from);
        return overlap > 0.6 * span && overlap > 0.6 * (era.to - era.from);
      })?.era ?? null
    );
  }

  update(camera: Camera): void {
    const here = this.current(camera);
    const { plot } = camera;
    for (const tab of this.tabs) {
      const a = Math.max(plot.left, camera.xOf(tab.era.from)),
        b = Math.min(plot.left + plot.width, camera.xOf(tab.era.to));
      const x = Math.round(a),
        w = Math.max(0, Math.round(b - a) - 2);
      const s = tab.shown;
      if (x !== s.x || w !== s.w) {
        tab.button.style.transform = `translateX(${x}px)`;
        tab.button.style.width = `${w}px`;
        s.x = x;
        s.w = w;
      }
      // An age too narrow for its name keeps its place but not its words.
      const narrow = w < tab.era.name.length * 8 + 24;
      if (narrow !== s.narrow) {
        tab.button.classList.toggle("narrow", narrow);
        s.narrow = narrow;
      }
      const active = here === tab.era;
      if (active !== s.active) {
        tab.button.classList.toggle("active", active);
        tab.button.setAttribute("aria-current", active ? "true" : "false");
        s.active = active;
      }
    }
  }
}

const fmt = (y: number) => (y < 0 ? `${-y} BC` : y < 1000 ? `AD ${y}` : `${y}`);
